import WorkflowSchema, { Node } from '../models/workflow-schema.js';
import { JobInputValues } from '../models/job.js';
import type { NodeInput } from './builder/types.js';

type WorkflowInputSpec = NodeInput;

// The launch context an entry may declare; workflow/create takes it only as inputs, checked against the caller
export function withLaunchContext(
  inputs: { [key: string]: WorkflowInputSpec } = {},
  values: JobInputValues,
  companyId: string,
  personId: string,
): JobInputValues {
  const context: JobInputValues = { COMPANY_ID: companyId, ASSIGNED_TO: personId };
  return { ...Object.fromEntries(Object.entries(context).filter(([key]) => key in inputs)), ...values };
}

export function requiredInputKeys(inputs: { [key: string]: WorkflowInputSpec } = {}): string[] {
  return Object.keys(inputs).filter((key) => {
    const spec = inputs[key];
    return typeof spec === 'string' || !spec.optional;
  });
}

/**
 * Resolve a workflow schema's entry point: the entry record for `entryKey`, with all of its
 * required inputs present in `inputValues`, and the start node it points at. Throws when the
 * entry is missing, inputs are missing, or the start node can't be found.
 */
export function resolveWorkflowEntry(
  workflowSchema: WorkflowSchema,
  entryKey: string,
  inputValues: JobInputValues,
): { entry: { start: string; inputs?: { [key: string]: WorkflowInputSpec } }; startNode: Node } {
  const entry = workflowSchema.entry[entryKey];
  if (!entry) throw new Error(`Unable to find entry for workflow: ${workflowSchema.name}`);

  const missingInputs = requiredInputKeys(entry.inputs).filter((inputKey) => !inputValues[inputKey]);
  if (missingInputs.length > 0) throw new Error(`Missing inputs: ${missingInputs.join(', ')}`);

  const start = entry.start.replace('node-', '');
  const startNode = workflowSchema.nodes.find((node) => node.id === start);
  if (!startNode) throw new Error(`Unable to find start node: ${startNode} for workflow ${workflowSchema.name}`);

  return { entry, startNode };
}
